import React, { useState, useEffect } from 'react';
import { Truck } from 'lucide-react';
import Page from '../../../components/Page';
import PeriodFilter from '../../../components/PeriodFilter';
import FreightAIInsightsDisplay from './FreightAIInsightsDisplay';
import { useAnalyzeFreightByPeriodMutation, MonthRange } from '../../../services/api';
import { useOwnerHeaders } from '../../../contexts/OwnerContext';
import { motion } from 'framer-motion';
import { AIAssistantButton } from '../../../components/AIAssistant';

// Cache configuration
const CACHE_KEY = 'freight_analysis_cache';
const CACHE_EXPIRY = 1000 * 60 * 60; // 1 hour

interface CachedData {
  data: any;
  timestamp: number;
  year: number;
  months: number[];
  company: string;
}

const FreightAnalysisPage: React.FC = () => {
  const ownerHeaders = useOwnerHeaders();
  const [aiAnalysisData, setAiAnalysisData] = useState<any>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [analyzeFreightByPeriod, { isLoading: isSearching, error: searchError }] = useAnalyzeFreightByPeriodMutation();

  // Load cached data on mount
  useEffect(() => {
    const cachedData = getCachedData();
    if (cachedData) {
      setAiAnalysisData(cachedData);
      setHasSearched(true);
    }
  }, []);

  const getCacheKey = (year: number, months: number[], company: string) => {
    return `${CACHE_KEY}_${company}_${year}_${months.sort().join('_')}`;
  };

  const getCachedData = (): any | null => {
    try {
      const keys = Object.keys(localStorage).filter(key => key.startsWith(CACHE_KEY));
      for (const key of keys) {
        const cached = localStorage.getItem(key);
        if (cached) {
          const parsedCache: CachedData = JSON.parse(cached);
          const now = Date.now();
          if (now - parsedCache.timestamp < CACHE_EXPIRY) {
            return parsedCache.data;
          } else {
            localStorage.removeItem(key);
          }
        }
      }
    } catch (error) {
      // Error occurred;
    }
    return null;
  };

  const setCachedData = (data: any, year: number, months: number[], company: string) => {
    try {
      const cacheKey = getCacheKey(year, months, company);
      const cacheData: CachedData = {
        data,
        timestamp: Date.now(),
        year,
        months,
        company
      };
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    } catch (error) {
      // Error occurred;
    }
  };

  const handlePeriodFilter = async (year: number, monthRanges: MonthRange[]) => {
    if (!ownerHeaders?.company) {
      // Error occurred;
      return;
    }

    try {
      setHasSearched(false);

      // Convert MonthRange[] to number[] (extract startMonth from each range)
      const selectedMonths = monthRanges.map(range => range.startMonth);

      const result = await analyzeFreightByPeriod({
        year,
        month_ranges: selectedMonths
      }).unwrap();

      if (result.success && result.data) {
        setAiAnalysisData(result.data);
        setHasSearched(true);

        // Save to cache
        setCachedData(result.data, year, selectedMonths, ownerHeaders.company);
      } else {
        setAiAnalysisData(null);
        setHasSearched(false);
      }
    } catch (error) {
      // Error occurred;
      setAiAnalysisData(null);
      setHasSearched(false);
    }
  };

  return (
    <Page title="🚛 Análise de Fretes com IA">
      <div className="space-y-6">
        {/* Period Filter */}
        <PeriodFilter onFilter={handlePeriodFilter} />

        {/* Loading State */}
        {isSearching && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center py-20"
          >
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mb-4"></div>
            <p className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
              🤖 IA analisando dados de fretes...
            </p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Processando dados reais e gerando insights estratégicos
            </p>
          </motion.div>
        )}

        {/* Error State */}
        {searchError && hasSearched === false && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 rounded-xl"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '2px solid #ef4444'
            }}
          >
            <div className="flex items-center space-x-3 mb-2">
              <span className="text-2xl">❌</span>
              <h3 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                Erro ao buscar dados
              </h3>
            </div>
            <p style={{ color: 'var(--text-secondary)' }}>
              {'error' in searchError && typeof searchError.error === 'string' 
                ? searchError.error 
                : 'Erro interno do servidor'}
            </p>
          </motion.div>
        )}

        {/* Empty State - Only show if searched but no data */}
        {!isSearching && hasSearched && !aiAnalysisData && !searchError && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20"
          >
            <Truck className="w-24 h-24 mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
            <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Nenhum dado encontrado
            </h3>
            <p className="text-center max-w-md" style={{ color: 'var(--text-secondary)' }}>
              Não foram encontrados dados de frete para o período selecionado.
              Tente selecionar outro período.
            </p>
          </motion.div>
        )}

        {/* Placeholder - Show before any search */}
        {!isSearching && !hasSearched && !aiAnalysisData && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20"
          >
            <Truck className="w-24 h-24 mb-4" style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
            <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
              Selecione um período para análise
            </h3>
            <p className="text-center max-w-md" style={{ color: 'var(--text-secondary)' }}>
              Escolha o ano e os meses desejados no filtro acima para visualizar
              a análise inteligente de fretes com IA.
            </p>
          </motion.div>
        )}

        {/* AI Analysis Display */}
        {!isSearching && hasSearched && aiAnalysisData && (
          <>
            {/* AI Assistant Button */}
            {(() => {
              // Contexto normalizado para Fretes
              const screenContext = 'Freight'

              return <AIAssistantButton screenContext={screenContext} />
            })()}

            <FreightAIInsightsDisplay
              data={aiAnalysisData}
              isLoading={isSearching}
              error={searchError}
            />
          </>
        )}
      </div>
    </Page>
  );
};

export default FreightAnalysisPage;
